import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { checkTrendyolStatus } from '../../../api/Trendyol';
import { IntegrationStatusBadge } from '../../../components/IntegrationStatus/IntegrationStatusBadge';
import { PartnerPlatformSettingsProps } from '../../../interfaces/Settings/PartnerPlatformSettingsProps';

export const TrendyolSettings = ({ onSave }: PartnerPlatformSettingsProps) => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [loadingStatus, setLoadingStatus] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.TRENDYOL)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => {
        refreshStatus();
        setLoading(false);
      });
  };

  const refreshStatus = () => {
    setLoadingStatus(true);
    checkTrendyolStatus()
      .then(() => setErrorMessage(undefined))
      .catch((err) => {
        setErrorMessage(err.message ?? 'Bilinmeyen hata');
      })
      .finally(() => setLoadingStatus(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.TRENDYOL, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
        onSave?.();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            ...sharedStyles.h5,
            fontWeight: 'bold',
          }}
        >
          Trendyol
        </Typography>

        <IntegrationStatusBadge
          status={
            loadingStatus ? 'loading' : errorMessage ? 'error' : 'success'
          }
          errorMessage={errorMessage}
        />
      </Box>
      <Divider />

      <SettingsRow
        name='Stok ve Fiyat Eşitleme'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'trendyol_status')}
        onChange={(newValue) => handleChange('trendyol_status', newValue)}
      />
      <SettingsRow
        name='Komisyon Oranı (%)'
        valueType={SettingsValueType.NUMBER}
        value={getSettingsValue(settings, 'trendyol_commision')}
        onChange={(newValue) => handleChange('trendyol_commision', newValue)}
        minValue={0}
        maxValue={100}
      />
      <SettingsRow
        name='TRENDYOL_API_KEY'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'trendyol_api_key')}
        onChange={(newValue) => handleChange('trendyol_api_key', newValue)}
        masked
      />
      <SettingsRow
        name='TRENDYOL_SECRET'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'trendyol_secret')}
        onChange={(newValue) => handleChange('trendyol_secret', newValue)}
        masked
      />
      <SettingsRow
        name='TRENDYOL_SUPPLIER_ID'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'trendyol_supplier_id')}
        onChange={(newValue) => handleChange('trendyol_supplier_id', newValue)}
      />
      <SettingsRow
        name='TRENDYOL_TOKEN'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'trendyol_token')}
        onChange={(newValue) => handleChange('trendyol_token', newValue)}
        masked
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
